import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { videos } from '@/mocks/videos';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 4);
  const relatedVideos = videos.slice(0, 6);

  if (!article) {
    return (
      <>
        <SEO
          title="文章未找到 - 菠萝视频"
          description="该文章可能已被删除或链接无效，返回菠萝视频首页浏览更多精彩内容。"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-foreground-500 mb-4">该文章可能已被删除或链接无效</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryMap: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
    documentary: '纪录片',
    kids: '少儿',
  };

  return (
    <>
      <SEO
        title={`${article.title} - 菠萝视频`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.boluovideo.com/article/${article.id}`}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryMap[article.category] || '分类', path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {article.views} 阅读
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-sm font-medium text-foreground-600">分享到：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微信分享">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博分享">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>

            {relatedVideos.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-5">推荐视频</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {relatedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-medium text-foreground-800 line-clamp-2 leading-snug">
                          {video.title}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">{video.rating} 分</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200">
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-5">相关文章</h3>
                <div className="space-y-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="flex items-start gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        className="w-24 h-16 md:w-32 md:h-20 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <h4 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 leading-snug">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-1">
                          {rel.summary}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
                          <span>{rel.date}</span>
                          <span>{rel.views} 阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
        <Footer />
      </div>
    </>
  );
}